import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw, Send, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useInquiriesApi, STATUS_META, NOTIFY_META, fmtDateTime } from '../../components/admin/inquiries/useInquiriesApi';
import { errMsg } from '../../components/admin/hiring/useHiringApi';
import { EmailActivity } from '../../components/admin/email/EmailActivity';
import { useAuth } from '../../context/AuthContext';

const NONE = '__none';

const NotificationPanel = ({ inq, onRetry, busy }) => {
  const n = inq.notification || {};
  const m = NOTIFY_META[n.status] || NOTIFY_META.pending;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-2" data-testid="inquiry-notification">
      <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Email notification</p>
      <p className={`text-sm font-semibold ${m.tone}`} data-testid="inquiry-notification-status">{m.label}</p>
      <p className="text-xs text-zinc-500">To {n.to || 'ContactUs@GleamGurus.com'} · Reply-To {inq.email} · {n.attempts || 0} attempt{n.attempts === 1 ? '' : 's'}{n.last_attempt_at ? ` · last ${fmtDateTime(n.last_attempt_at)}` : ''}</p>
      {n.error && <p className="text-xs text-red-600 break-words" data-testid="inquiry-notification-error">{n.error}</p>}
      {n.status !== 'sent' && <Button size="sm" variant="outline" disabled={busy} onClick={onRetry} data-testid="inquiry-retry-notification"><RefreshCw className="w-4 h-4 mr-1" /> Retry notification</Button>}
    </div>
  );
};

const Notes = ({ inq, onAdd }) => {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!body.trim()) return;
    setSaving(true);
    if (await onAdd(body.trim())) setBody('');
    setSaving(false);
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid="inquiry-notes">
      <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Internal notes · staff only</p>
      {(inq.notes || []).length === 0 && <p className="text-sm text-zinc-400">No notes yet.</p>}
      {[...(inq.notes || [])].reverse().map((n) => (
        <div key={n.id} className="border-l-2 border-orange-300 pl-3" data-testid={`inquiry-note-${n.id}`}>
          <p className="text-sm text-zinc-800 whitespace-pre-wrap">{n.body}</p>
          <p className="text-[11px] text-zinc-400">{n.actor_name} · {fmtDateTime(n.at)}</p>
        </div>
      ))}
      <Textarea rows={3} maxLength={4000} placeholder="Add an internal note" value={body} onChange={(e) => setBody(e.target.value)} data-testid="inquiry-note-input" />
      <Button size="sm" disabled={saving || !body.trim()} onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="inquiry-note-save"><Send className="w-4 h-4 mr-1" /> Add note</Button>
    </div>
  );
};

export default function AdminInquiryPage() {
  const { id } = useParams();
  const api = useInquiriesApi();
  const [inq, setInq] = useState(null);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const { token } = useAuth();
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try { const [d, s] = await Promise.all([api.get(id), api.staff()]); setInq(d); setStaff(s); }
    catch (e) { toast.error(errMsg(e, 'Could not load inquiry')); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { setInq(await fn()); if (ok) toast.success(ok); return true; } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };

  if (!inq) return <><p className="p-8 text-sm text-zinc-500" data-testid="inquiry-loading">Loading…</p></>;
  const replyHref = `mailto:${inq.email}?subject=${encodeURIComponent('Re: Your message to Gleam Guru')}`;

  return (
    <>
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="admin-inquiry-page">
        <div className="flex items-start gap-3">
          <Link to="/admin/inquiries" className="text-zinc-500 hover:text-zinc-900 mt-1" data-testid="back-to-inquiries"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-zinc-900 break-words" data-testid="inquiry-name">{inq.name}</h1>
            <p className="text-sm text-zinc-500 break-all"><a href={replyHref} className="hover:text-orange-700" data-testid="inquiry-email">{inq.email}</a> · Received {fmtDateTime(inq.created_at)}</p>
          </div>
          <Button asChild size="sm" variant="outline"><a href={replyHref} data-testid="inquiry-reply"><Mail className="w-4 h-4 mr-1" /> Reply by email</a></Button>
        </div>
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-2">Message</p>
              <p className="text-sm text-zinc-800 whitespace-pre-wrap break-words" data-testid="inquiry-message">{inq.message}</p>
            </div>
            <Notes inq={inq} onAdd={(body) => run(() => api.note(id, body), 'Note added')} />
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
              <label className="block text-xs font-semibold text-zinc-600 space-y-1"><span>Status</span>
                <Select value={inq.status} onValueChange={(v) => run(() => api.update(id, { status: v }), 'Status updated')} disabled={busy}>
                  <SelectTrigger data-testid="inquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`inquiry-status-${k}`}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="block text-xs font-semibold text-zinc-600 space-y-1"><span>Assigned to</span>
                <Select value={inq.assignee_id || NONE} disabled={busy}
                  onValueChange={(v) => run(() => api.update(id, v === NONE ? { clear_assignee: true } : { assignee_id: v }), 'Assignment updated')}>
                  <SelectTrigger data-testid="inquiry-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>Unassigned</SelectItem>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              {inq.lead_id ? (
                <Link to={`/admin/quote-requests?id=${inq.lead_id}`} className="block text-sm font-semibold text-sky-700 hover:underline" data-testid="inquiry-lead-link">Moved to leads · open lead</Link>
              ) : (
                <Button size="sm" variant="outline" className="w-full" disabled={busy}
                  onClick={() => window.confirm('Create a lead from this inquiry? Only do this if the person is asking about cleaning services.') && run(() => api.moveToLead(id), 'Lead created')}
                  data-testid="inquiry-move-to-lead"><UserPlus className="w-4 h-4 mr-1" /> Move to leads</Button>
              )}
            </div>
            <NotificationPanel inq={inq} busy={busy} onRetry={() => run(() => api.retry(id))} />
            <EmailActivity inquiryId={id} headers={headers} />
            <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="inquiry-activity">
              <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-2">Activity</p>
              {[...(inq.activity || [])].reverse().map((a) => (
                <p key={a.id} className="text-xs text-zinc-600 py-1 border-t border-zinc-100 first:border-0">{a.action.replace(/_/g, ' ')}{a.details?.to ? ` → ${STATUS_META[a.details.to]?.label || a.details.to}` : ''} · {a.actor_name} · {fmtDateTime(a.at)}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
